import { DefaultButton, Dropdown, IDropdownOption, MessageBar, Pivot, PivotItem, Spinner, Stack, StackItem } from '@fluentui/react';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import PersonalSettingsState from '../../../state/PersonalSettingsState';
import { InfoTooltipLabel } from '../../controls/InfoTooltipLabel';
import PermissionsList from './PermissionsList';
import NewPeoplePicker, { PeoplePickerIdFormat } from '../../controls/NewPeoplePicker';
import { apiConfig } from '../../../authConfig';

function PermissionsPage({ state }: { state: PersonalSettingsState }) {

	useEffect(() => {
		state.ensureLoaded();
		state.ensureServiceAccountInfoLoaded();
	}, [state]);

	if(state.loading) {
		return <Spinner />
	}

	let dropdownOptions:IDropdownOption[] = [{
		text: 'Use service account user',
		key: 'serviceaccount'
	},{
		text: 'Use application permissions',
		key: 'app'
	}];

	return (
		<Stack>			
			<Pivot>
				<PivotItem headerText='End-user (delegated)'>
					<MessageBar>Delegated permissions allow the application to access resources with the active end user identity. These are required for the application to function correctly.</MessageBar>
					<PermissionsList state={state.delegated} />
				</PivotItem>
				
				<PivotItem headerText='Background tasks'>
					<MessageBar>Application permissions allow the software to operate automatically in the background when no user is present. Without them, you must specify a service account.</MessageBar>

					<Stack horizontal>
						<InfoTooltipLabel label='Background task permissions mode' title='Select to enable application permissions. They will need to be granted below when this is enabled.'>
							<Dropdown options={dropdownOptions} defaultSelectedKey={state.settings.useAppIdentity ? 'app' : 'serviceaccount'} onChange={(_,o) => {  state.updateUseAppIdentity(o?.key === 'app') }} />
						</InfoTooltipLabel>
					</Stack>

					{ state.settings.useAppIdentity ? <></> : <>

						<InfoTooltipLabel label='Service User' title='Select the user ID whose identity will be used to perform background operations. Note that their access token must be kept up to date.'>
							
							<NewPeoplePicker 
								format={PeoplePickerIdFormat.EntraID}
								siteUrl={state.settings.entityTypes[0]?.sharePointListUrl || ''} 
								config={state.userConfig} 
								initialPeople={state.settings.serviceAccountId ? [state.settings.serviceAccountId] : []} 
								updatePeople={(people) => state.updateServiceAccountId(people[0] ?? '')} 
							/>

							<InfoTooltipLabel label='Service User Token' title='The access token for the service user. This must be kept up to date. You can share this Sign in link with another user as required.'>
								<span>{state.svcAccTokenInfo?.token ? '✅ Configured' : ' ❗Not Configured'}</span> <span>
									<a href={apiConfig.uri.replace('/api', '') +  `/Login/Start?userId=${encodeURIComponent(state.settings.serviceAccountId)}&tenantId=${encodeURIComponent(state.settings.tenantId)}`} rel='noopener noreferrer' target='_blank'>Sign in to update token</a>
								</span>
							</InfoTooltipLabel>

						</InfoTooltipLabel>
					</> }


					<Stack horizontal style={{marginTop:10}}>
						<DefaultButton disabled={!state.serviceAccountChangesMade} onClick={() => state.saveServiceAccountChanges()}>Apply background task permission changes</DefaultButton>
					</Stack>

					{state.settings.useAppIdentity ? <PermissionsList state={state.app} /> : <></> }
			</PivotItem>

			<PivotItem headerText='Admins'>
				<InfoTooltipLabel label='Admin users' title='Select the users who have admin access to this application. Your own account will always be included when saving.'>
					<NewPeoplePicker
						format={PeoplePickerIdFormat.EntraID}
						siteUrl={state.settings.entityTypes[0]?.sharePointListUrl || ''}
						config={state.userConfig}
						initialPeople={state.adminUserIds}
						updatePeople={(ids) => state.setAdminUserIds(ids)}
					/>
				</InfoTooltipLabel>
				<Stack horizontal style={{ marginTop: 10 }}>
					<StackItem>
						<DefaultButton disabled={state.isSavingAdmins} onClick={() => state.saveAdmins()}>Save</DefaultButton>
					</StackItem>
					{state.isSavingAdmins && <StackItem style={{ marginLeft: 10 }}><Spinner /></StackItem>}
				</Stack>
			</PivotItem>
		</Pivot>

		</Stack>
	);
}


export default observer(PermissionsPage);
